import React, { useState } from 'react';

import { Box } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import CompressedProgressBar from './CompressedProgressBar';
import RolloverProgressSteps from './RolloverProgressSteps';

import { GraphQLProvider } from '../../RetailApp/graphql';
import { IdempotencyProviderWithFeatureRegistry } from '../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { urlBuilder } from '../../Shared/urlBuilder';
import useCapitalizeWindowEvents from '../hooks/useCapitalizeWindowEvents';
import useSerializedWindowEventMutationHandler from '../hooks/useSerializedWindowEventMutationHandler';

const STEPS = [
  { label: 'Select account' },
  { label: 'Add provider info' },
  { label: 'Submit rollover' },
  { label: 'Track rollover' },
];

export interface CapitalizeWebviewContentProps {
  webviewUrl: string;
  internalRolloverRedirectPath: string;
  exitPath: string;
}

export const CapitalizeWebviewContent: React.FC<CapitalizeWebviewContentProps> = ({
  webviewUrl,
  internalRolloverRedirectPath,
  exitPath,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const processWindowEvent = useSerializedWindowEventMutationHandler();

  useCapitalizeWindowEvents({
    onPartnerInternalRolloverRedirect: () => {
      window.location.href = internalRolloverRedirectPath;
    },
    onError: event => {
      window.console.error('Capitalize webview error', event.onError);
    },
    onCapitalizeRolloverSubmittedV2: event => {
      setCurrentStep(3);
      processWindowEvent(event);
    },
    onCapitalizeRolloverCreated: event => {
      setCurrentStep(2);
      processWindowEvent(event);
    },
    onCapitalizeExit: () => {
      window.location.href = exitPath;
    },
    onCreateRothDestinationNeeded: () => {
      window.location.href = urlBuilder.appAddGoalFlowsNew.absolute({ product_category: 'retirement' });
    },
    onCreateTradDestinationNeeded: () => {
      window.location.href = urlBuilder.appAddGoalFlowsNew.absolute({ product_category: 'retirement' });
    },
    onCapitalizeRolloverCanceled: processWindowEvent,
  });

  const percentage = Math.round(((currentStep + 1) / STEPS.length) * 100);

  return (
    <Box css={{ width: '100%' }}>
      <Box data-variant="compressed">
        <CompressedProgressBar
          percentage={percentage}
          step={STEPS[currentStep].label}
          currentStep={currentStep + 1}
          totalSteps={STEPS.length}
        />
      </Box>
      <Box css={{ position: 'relative' }}>
        <Box
          data-variant="full"
          css={{
            position: 'absolute',
            top: 'space.300',
            left: 0,
          }}
        >
          <RolloverProgressSteps steps={STEPS} currentStep={currentStep} />
        </Box>
        <iframe
          className={css({
            display: 'block',
            width: '50%',
            marginLeft: 'auto',
            marginRight: 'auto',
            height: '90vh',
            border: '1px solid',
            borderColor: 'border.weak',
            overflow: 'hidden',
            mdDown: {
              width: '100%',
            },
          })}
          src={webviewUrl}
          title="Capitalize rollover webview"
        />
      </Box>
    </Box>
  );
};

const CapitalizeWebviewContentWithIdempotency: React.FC<CapitalizeWebviewContentProps> = props => {
  return (
    <GraphQLProvider>
      <IdempotencyProviderWithFeatureRegistry featureName="capitalizeRollovers">
        <CapitalizeWebviewContent {...props} />
      </IdempotencyProviderWithFeatureRegistry>
    </GraphQLProvider>
  );
};

export default CapitalizeWebviewContentWithIdempotency;
